import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FeedPerson } from "@signalist/shared-types";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
	closeContactLeadPanel,
	openContactLeadPanel,
} from "@/pages/Private/redux/contact/contact.slice";
import { useLazyGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";
import { GenerationJob } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { feedSelector, setFeedSelectedCardId } from "@/pages/Private/redux/feed/feed.slice";

import { feedCardKey } from "./feedCardKey";
import { feedPersonToContactPreview } from "./feedPersonToContactPreview";
import { generationJobToContactPreview } from "./generationJobToContactPreview";

interface FeedDockedPanel {
	/** The selected card's current person — the docked panel's target (D5/D6). */
	panelPerson: FeedPerson | null;
	/** listPersonId driving the panel's Chat tab — a pill job override wins (P6 D6). */
	panelChatListPersonId: number | null;
	/**
	 * Open the panel for a queue-pill job whose card is not on the current page
	 * (P6 D6): minimal preview from the job's display fields, no pager change.
	 */
	openForJob: (job: GenerationJob) => void;
	/** Grabber close: shuts the panel and clears the card selection (D6). */
	closePanel: () => void;
}

/** The card/person identity a job-panel open was made under (see below). */
const selectionToken = (selectedCardId: number | null, listPersonId: number | undefined) =>
	`${selectedCardId}:${listPersonId}`;

/**
 * Drives the docked `ContactLeadPanel` from the feed selection (D6): opens the
 * panel with a preview built from the selected card's person, lazy-fetches the
 * contact detail (aborting the previous in-flight one — GlobalSearchModal
 * pattern) and re-targets on card click or person switch. A person without a
 * prospect keeps the card selected but closes/never opens the panel (D6/D7).
 *
 * P6 adds {@link FeedDockedPanel.openForJob}: the job panel stays active only
 * while the card selection it was opened under is unchanged — any later card
 * click or person switch re-targets the panel and the job override derives
 * away (no effect-driven state clearing).
 */
export const useFeedDockedPanel = (): FeedDockedPanel => {
	const dispatch = useAppDispatch();
	const { cards, selectedCardId, selectedPersonByRunId } = useAppSelector(feedSelector);
	const [triggerGetContactProspectDetail] = useLazyGetContactProspectDetailQuery();
	const detailQueryRef = useRef<{ abort: () => void } | null>(null);
	const hadSelectionRef = useRef(false);

	const selectedCard = useMemo(
		() => cards.find((card) => feedCardKey(card) === selectedCardId) ?? null,
		[cards, selectedCardId]
	);

	const panelPerson = useMemo(() => {
		if (!selectedCard) {
			return null;
		}

		return (
			selectedCard.people.find(
				(person) => person.listPersonId === selectedPersonByRunId[feedCardKey(selectedCard)]
			) ??
			selectedCard.people[0] ??
			null
		);
	}, [selectedCard, selectedPersonByRunId]);

	// Off-page job panel (P6 D6), tagged with the selection identity it was
	// opened under — a mismatch means the user re-targeted the panel since.
	const [jobPanel, setJobPanel] = useState<{ job: GenerationJob; token: string } | null>(null);
	const currentToken = selectionToken(selectedCardId, panelPerson?.listPersonId);
	const activeJobPanel = jobPanel?.token === currentToken ? jobPanel.job : null;

	useEffect(() => {
		if (!selectedCard) {
			// Selection cleared (tab/list change, ignored card) — close the panel
			// we opened; never touch it before the first feed selection.
			if (hadSelectionRef.current) {
				dispatch(closeContactLeadPanel());
				hadSelectionRef.current = false;
			}

			return;
		}

		hadSelectionRef.current = true;

		const preview = panelPerson ? feedPersonToContactPreview(panelPerson, selectedCard) : null;

		if (!preview) {
			dispatch(closeContactLeadPanel());

			return;
		}

		dispatch(openContactLeadPanel(preview));
		detailQueryRef.current?.abort();
		detailQueryRef.current = triggerGetContactProspectDetail(preview.id);
		// Re-target on identity changes only — a page refetch rebuilding the same
		// card must not re-open/refetch the panel. The prospectId IS an identity
		// change: selecting a prospect for a placeholder lead upgrades the same
		// list person to a new prospect, and the panel must follow.
	}, [
		dispatch,
		triggerGetContactProspectDetail,
		selectedCard?.runId,
		panelPerson?.listPersonId,
		panelPerson?.prospectId,
	]);

	const openForJob = useCallback(
		(job: GenerationJob) => {
			setJobPanel({ job, token: currentToken });
			dispatch(openContactLeadPanel(generationJobToContactPreview(job)));
			detailQueryRef.current?.abort();
			detailQueryRef.current = triggerGetContactProspectDetail(job.prospectId);
		},
		[dispatch, triggerGetContactProspectDetail, currentToken]
	);

	const closePanel = useCallback(() => {
		detailQueryRef.current?.abort();
		setJobPanel(null);
		dispatch(closeContactLeadPanel());
		dispatch(setFeedSelectedCardId(null));
	}, [dispatch]);

	return {
		panelPerson,
		panelChatListPersonId: activeJobPanel?.listPersonId ?? panelPerson?.listPersonId ?? null,
		openForJob,
		closePanel,
	};
};
